import React, { useCallback, useMemo } from "react";
import * as Yup from "yup";
import {
  WSActions,
  WSCard,
  WSForm,
  WSInfoBox,
  WSInputTextarea,
  WSList,
  WSSectionToolbar,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { GetMissingFieldsLevelParameter } from "@wingspanhq/wingspan-api-sdk";
import {
  EDD_FIELDS,
  EDDQuestionnaireFormData,
  ProhibitedJurisdictionExposure
} from "./utils";
import { ErrorAlert } from "@components/ErrorAlert";
import { complianceEntitiesApi, onboardingApi } from "@api";
import { queriesOnboarding } from "@query";
import { useQueryPrimaryComplianceEntity } from "../../../../shared/hooks/useQueryPrimaryComplianceEntity";

interface EDDQuestionnaireProps {
  onCancel?: () => void;
  onSubmit?: (data: EDDQuestionnaireFormData) => void;
  isLoading?: boolean;
}

export const EDDQuestionnaire: React.FC<EDDQuestionnaireProps> = ({
  onCancel,
  onSubmit,
  isLoading = false
}) => {
  const queryMissingDataEnhanced = queriesOnboarding.getMissingFields.useQuery({
    level: GetMissingFieldsLevelParameter.ENHANCED
  });
  const queryVerifications = queriesOnboarding.getOnboardingStatus.useQuery(
    undefined,
    { select: status => status.verifications }
  );
  const queryComplianceEntity = useQueryPrimaryComplianceEntity();
  const complianceEntityId = queryComplianceEntity.data?.id;

  const handleSubmit = useCallback(
    async (data: EDDQuestionnaireFormData) => {
      if (!complianceEntityId) {
        throw new Error("No compliance entity found");
      }

      const { prohibitedJurisdictionExposureDetails, ...answers } = data;

      await complianceEntitiesApi.updateComplianceEntity({
        complianceEntityId,
        complianceEntityUpdate: {
          enhancedDueDiligence: {
            ...answers,
            prohibitedJurisdictionExposureDetails:
              answers.prohibitedJurisdictionExposure ===
              ProhibitedJurisdictionExposure.TRUE
                ? prohibitedJurisdictionExposureDetails?.trim()
                : undefined
          }
        }
      });

      await onboardingApi.triggerVerification({
        verifyRequest: { level: GetMissingFieldsLevelParameter.ENHANCED }
      });

      await queryVerifications.refetch();
      await queryMissingDataEnhanced.refetch();

      onSubmit?.(data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSubmit, complianceEntityId]
  );

  const handleCancel = useCallback(async () => {
    onCancel?.();
  }, [onCancel]);

  const validationSchema = useMemo(() => {
    let shape = {};

    EDD_FIELDS.filter(field =>
      queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
    ).forEach(field => {
      const fieldSchema =
        field.componentProps.required !== false
          ? Yup.string().required("This field is required")
          : Yup.string();

      shape = {
        ...shape,
        [field.name]: fieldSchema
      };

      if (field.name === "prohibitedJurisdictionExposure") {
        shape = {
          ...shape,
          prohibitedJurisdictionExposureDetails: Yup.string().when(
            "prohibitedJurisdictionExposure",
            {
              is: ProhibitedJurisdictionExposure.TRUE,
              then: Yup.string().trim().required("This field is required"),
              otherwise: Yup.string()
            }
          )
        };
      }
    });

    return Yup.object().shape(shape);
  }, [queryMissingDataEnhanced.data?.requiredFields]);

  const defaultValues = useMemo(() => {
    let defaultValues = {};

    EDD_FIELDS.filter(field =>
      queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
    ).forEach(field => {
      defaultValues = {
        ...defaultValues,
        [field.name]: ""
      };

      if (field.name === "prohibitedJurisdictionExposure") {
        defaultValues = {
          ...defaultValues,
          prohibitedJurisdictionExposureDetails: ""
        };
      }
    });

    return defaultValues;
  }, [queryMissingDataEnhanced.data?.requiredFields]);

  if (!queryMissingDataEnhanced.isSuccess) {
    return null;
  }

  return (
    <>
      <WSSectionToolbar title="Complete questionnaire"></WSSectionToolbar>
      <WSForm<EDDQuestionnaireFormData>
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        defaultValues={defaultValues}
      >
        <WSList gap="L">
          <WSInfoBox
            theme="neutral"
            icon="info-circle"
            title="Why am I seeing this?"
          >
            To pay contractors/invoice clients, we need some additional
            information.
          </WSInfoBox>

          {EDD_FIELDS.filter(field =>
            queryMissingDataEnhanced.data?.requiredFields?.includes(field.name)
          ).map(({ title, name, label, componentProps }, index) => (
            <WSCard key={name} header={`${index + 1}. ${title}`}>
              <WSForm.Field
                name={name}
                label={label}
                component={WSSelect}
                componentProps={{
                  placeholder: "Select an option",
                  required: componentProps.required !== false,
                  ...componentProps
                }}
              />
              {name === "prohibitedJurisdictionExposure" && (
                <WSForm.Value name="prohibitedJurisdictionExposure">
                  {prohibitedJurisdictionExposure =>
                    prohibitedJurisdictionExposure ===
                    ProhibitedJurisdictionExposure.TRUE ? (
                      <WSForm.Field
                        mt="M"
                        name="prohibitedJurisdictionExposureDetails"
                        label="Please describe"
                        component={WSInputTextarea}
                        componentProps={{
                          required: true,
                          placeholder:
                            "Describe the clients, contractors, vendors, or payments in these jurisdictions"
                        }}
                      />
                    ) : null
                  }
                </WSForm.Value>
              )}
            </WSCard>
          ))}

          <WSCard>
            <WSText.ParagraphSm>
              At Wingspan, we are required by law to verify your business. This
              helps us prevent fraud and comply with regulations. Your
              cooperation ensures a safe and secure experience for everyone.
              Thank you for providing the necessary information and documents.
            </WSText.ParagraphSm>
            <WSText.ParagraphSm mt="M">
              The review period typically takes about one (1) business day,
              during this time you may not be able to send/receive payments or
              access banking features.
            </WSText.ParagraphSm>
          </WSCard>

          <ErrorAlert error={queryMissingDataEnhanced.error} context="Other" />

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                onClick: handleCancel,
                label: "Cancel",
                type: "button",
                disabled: isLoading
              },
              {
                label: "Continue",
                loading: isLoading,
                type: "submit"
              }
            ]}
          />

          <WSText.ParagraphXs color="gray400" mt="M">
            This form is meant for the recipient listed at the top of this page.
            Please disregard and notify Wingspan immediately if this is not you.
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </>
  );
};
